/**
 * Picks the face inference engine for the session and exposes one
 * interface to useFaceDetection.js.
 *
 * The engine is chosen once, the first time anything asks, and cached
 * for the page load. Flags arrive from the edge before any capture
 * screen mounts, but pinning the choice guarantees a session cannot
 * switch engines halfway through and produce a mixed-arm funnel.
 */
import { flagEnabled, flagsKnown, whenFlagsKnown, FACE_ENGINE_MEDIAPIPE } from "../../utils/featureFlags";
import * as faceApiEngine from "./faceApiEngine";

export const ENGINE_FACE_API = "face-api";
export const ENGINE_MEDIAPIPE = "mediapipe";

let _pinned = null;
let _module = null;
let _modulePromise = null;

// QA override so the arm can be forced on a shared preview link without
// touching the Flagship rollout: ?face_engine=mediapipe | face-api.
// Anything else is ignored and the flag decides.
const engineFromUrl = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get("face_engine");
    if (raw === ENGINE_MEDIAPIPE || raw === ENGINE_FACE_API) return raw;
  } catch {
    // no window (SSR/tests) or a malformed query string
  }
  return null;
};

/**
 * The pinned engine, or a provisional face-api answer if nothing has
 * pinned yet. Pinning happens in one of two places only: a URL override
 * (immediately) or the flag, and the flag is consulted only once the
 * edge has delivered it. Before that this returns face-api WITHOUT
 * pinning, so an early caller (a capture screen's mount effect runs
 * before DataContext's flag effect on the same commit) cannot lock the
 * session into the control arm before the split was ever read. That
 * exact race put 0 of 222 sessions in the test arm on 2026-09-16 while
 * Flagship reported a clean 5.77% "on".
 */
export function getEngineName() {
  if (_pinned) return _pinned;
  const fromUrl = engineFromUrl();
  if (fromUrl) {
    _pinned = fromUrl;
    return _pinned;
  }
  if (flagsKnown()) {
    _pinned = flagEnabled(FACE_ENGINE_MEDIAPIPE) ? ENGINE_MEDIAPIPE : ENGINE_FACE_API;
    return _pinned;
  }
  return ENGINE_FACE_API;
}

/**
 * Waits for the flags (bounded), then pins. Everything that loads a
 * model goes through this so the arm is decided exactly once, after
 * the split is known, and never on a guess.
 */
export function resolveEngineName() {
  if (_pinned) return Promise.resolve(_pinned);
  if (engineFromUrl()) return Promise.resolve(getEngineName());
  return whenFlagsKnown(3000).then(() => {
    if (!_pinned) {
      // Flags arrived, or the timeout fired and defaults stand: either
      // way pin now so the answer is stable for the rest of the page.
      _pinned = flagEnabled(FACE_ENGINE_MEDIAPIPE) ? ENGINE_MEDIAPIPE : ENGINE_FACE_API;
    }
    return _pinned;
  });
}

/**
 * Resolves the pinned engine's module, importing MediaPipe's only if
 * that arm is active.
 *
 * face-api stays a static import and MediaPipe is split out, so the
 * control arm's main bundle is byte-identical to what shipped before
 * this experiment existed. Importing both statically cost every session
 * ~48KB gzipped of MediaPipe wrapper — including the control arm, which
 * never runs it — and a bundle regression carried by both arms is
 * exactly the kind of thing that quietly moves an A/B's baseline. The
 * test arm now pays for its own wrapper, in its own chunk, which is
 * where that cost belongs.
 */
export function loadEngine() {
  if (_modulePromise) return _modulePromise;
  _modulePromise = resolveEngineName()
    .then((name) => {
      if (name === ENGINE_FACE_API) return faceApiEngine;
      return import("./mediapipeEngine");
    })
    .then((mod) => {
      _module = mod;
      return mod;
    })
    .catch((err) => {
      // Reset so a later caller can retry, and re-throw so the current
      // caller still sees the failure.
      _modulePromise = null;
      throw err;
    });
  return _modulePromise;
}

/**
 * The already-resolved engine, for synchronous callers. Only valid once
 * loadEngine() has settled — the detection loop is gated on modelsLoaded,
 * which is set from that promise, so it always is by then.
 */
export function getLoadedEngine() {
  return _module;
}
